import { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../api';

const STATUS_COLORS = {
  PENDING: 'bg-yellow-100 text-yellow-800',
  CONFIRMED: 'bg-blue-100 text-blue-800',
  PROCESSING: 'bg-purple-100 text-purple-800',
  SHIPPED: 'bg-indigo-100 text-indigo-800',
  DELIVERED: 'bg-green-100 text-green-800',
  CANCELLED: 'bg-red-100 text-red-800',
  REFUNDED: 'bg-gray-100 text-gray-800',
};

export const getStatusColor = (status) => STATUS_COLORS[status] || 'bg-gray-100 text-gray-800';

/**
 * Owns the order list, the order-details/assign/cancel modals, and the
 * status-transition + delivery-assignment handlers. Needs `deliveryPersons`
 * and `fetchDeliveryPersons` from useDelivery (the assign modal picks from
 * that same roster) rather than keeping its own copy.
 */
export default function useOrders({ activeTab, showNotification, fetchDeliveryPersons }) {
  const [orders, setOrders] = useState([]);
  const [orderFilter, setOrderFilter] = useState('all');
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignOrderId, setAssignOrderId] = useState(null);

  // Cancellation modal state
  const [showCancelOrderModal, setShowCancelOrderModal] = useState(false);
  const [cancelOrderId, setCancelOrderId] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelCustomReason, setCancelCustomReason] = useState('');
  const [refundReasons, setRefundReasons] = useState([]);
  const [loadingRefundReasons, setLoadingRefundReasons] = useState(false);

  const fetchOrders = async () => {
    setLoadingOrders(true);
    try {
      const token = localStorage.getItem('adminToken');
      // Fetch all orders by setting a high limit
      const response = await fetch(`${API_BASE_URL}/api/admin/orders?limit=1000`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const data = await response.json();
        setOrders(data.orders || []);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoadingOrders(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'orders') {
      fetchOrders();
      fetchDeliveryPersons();
    }
  }, [activeTab]);

  const handleViewOrder = async (orderId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/orders/${orderId}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        setSelectedOrder(await response.json());
        setShowOrderDetails(true);
      }
    } catch (error) {
      console.error('Error fetching order details:', error);
      showNotification('Failed to load order details', 'error');
    }
  };

  const closeOrderDetails = () => {
    setShowOrderDetails(false);
    setSelectedOrder(null);
  };

  const handleUpdateOrderStatus = async (orderId, status, deliveryPersonId = null, deliveryDays = null, notes = '') => {
    setLoadingOrders(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/orders/${orderId}/status`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, deliveryPersonId, deliveryDays, notes }),
      });

      if (response.ok) {
        await fetchOrders();
        if (showOrderDetails && selectedOrder && selectedOrder.id === orderId) {
          const detailResponse = await fetch(`${API_BASE_URL}/api/admin/orders/${orderId}`, {
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          });
          if (detailResponse.ok) setSelectedOrder(await detailResponse.json());
        }
        showNotification(`Order status updated to ${status}`, 'success');
        if (showOrderDetails) closeOrderDetails();
      } else {
        const error = await response.json();
        showNotification(error.error || 'Failed to update order status', 'error');
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      showNotification('Network error. Please try again.', 'error');
    } finally {
      setLoadingOrders(false);
    }
  };

  const openAssignModal = (orderId) => {
    fetchDeliveryPersons();
    setAssignOrderId(orderId);
    setShowAssignModal(true);
  };

  const closeAssignModal = () => {
    setShowAssignModal(false);
    setAssignOrderId(null);
  };

  const handleAssign = async (orderId, personnelId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/orders/${orderId}/assign`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ personnelId }),
      });
      if (response.ok) {
        showNotification('Assigned successfully', 'success');
        await fetchOrders();
        await fetchDeliveryPersons();
        closeAssignModal();
      } else {
        const err = await response.json();
        showNotification(err.error || 'Failed to assign', 'error');
      }
    } catch (error) {
      console.error('Error assigning delivery person:', error);
      showNotification('Network error. Please try again.', 'error');
    }
  };

  const fetchRefundReasons = async () => {
    setLoadingRefundReasons(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/refunds/reasons/list`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (response.ok) setRefundReasons(await response.json());
    } catch (error) {
      console.error('Error fetching refund reasons:', error);
    } finally {
      setLoadingRefundReasons(false);
    }
  };

  const openCancelOrderModal = (orderId) => {
    setCancelOrderId(orderId);
    setCancelReason('');
    setCancelCustomReason('');
    if (refundReasons.length === 0) fetchRefundReasons();
    setShowCancelOrderModal(true);
  };

  const closeCancelOrderModal = () => {
    setShowCancelOrderModal(false);
    setCancelOrderId(null);
    setCancelReason('');
    setCancelCustomReason('');
  };

  // Cancel order (admin) — opens the cancellation modal
  const handleCancelOrder = (orderId) => openCancelOrderModal(orderId);

  const handleCancelOrderSubmit = async () => {
    if (!cancelOrderId) return;
    const reason = cancelReason === 'OTHER' ? cancelCustomReason : cancelReason;

    try {
      const token = localStorage.getItem('adminToken');
      const resp = await fetch(`${API_BASE_URL}/api/admin/orders/${cancelOrderId}/cancel`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: reason || undefined }),
      });

      if (resp.ok) {
        const data = await resp.json();
        await fetchOrders();
        closeOrderDetails();
        closeCancelOrderModal();
        const message = data.refund
          ? 'Order cancelled and refund request created'
          : 'Order cancelled (refund request may be created if payment exists)';
        showNotification(message, 'success');
      } else {
        const err = await resp.json();
        showNotification(err.error || 'Failed to cancel order', 'error');
      }
    } catch (e) {
      console.error('Error cancelling order:', e);
      showNotification('Network error while cancelling order', 'error');
    }
  };

  return {
    orders, orderFilter, setOrderFilter, loadingOrders,
    selectedOrder, showOrderDetails, handleViewOrder, closeOrderDetails,
    showAssignModal, assignOrderId, openAssignModal, closeAssignModal, handleAssign,
    showCancelOrderModal, cancelOrderId, cancelReason, setCancelReason, cancelCustomReason, setCancelCustomReason,
    refundReasons, loadingRefundReasons, openCancelOrderModal, closeCancelOrderModal, handleCancelOrder, handleCancelOrderSubmit,
    handleUpdateOrderStatus, getStatusColor,
  };
}
